import { isEmpty, isNil, omitBy } from 'lodash';
import { type ActionCreatorsMapObject, bindActionCreators, type Store } from 'redux';
import { getPricingPlansPublicAPI } from '@wix/pricing-plans-tpa-api';
import { type IWixAPI } from '@wix/yoshi-flow-editor';
import { type SeoTagsPayload } from '@wix/native-components-infra/dist/src/types/types';
import {
  FETCH_BLOG_DATA_SUCCESS,
  biFileDownloaded,
  fetchAppDataSuccess,
  fetchTPASettings,
} from '@wix/communities-blog-client-common';
import {
  fetchArchivePostsWithAdapterPromisified,
  fetchCategoryPostsWithAdapterPromisified,
  fetchFeedPostsWithAdapterPromisified,
  fetchHashtagPostsWithAdapterPromisified,
  fetchTagPostsWithAdapterPromisified,
} from '../../feed-page/actions/fetch-feed-render-model';
import { actionsClosed } from '../actions/actions-closed';
import { actionsOpened } from '../actions/actions-opened';
import { emitBlogOpened } from '../actions/blog-socket';
import { buttonClicked } from '../actions/button-clicked';
import { categoryLabelClicked } from '../actions/category-label-clicked';
import { fetchFeedMetadata } from '../actions/fetch-feed-metadata';
import { fetchMentionsPromisified } from '../actions/fetch-mentions';
import { fetchPostListMetadata } from '../actions/fetch-post-list-metadata';
import { fetchPostMetadataPromisified } from '../actions/fetch-post-metadata';
import { headerSearchClosed, headerSearchOpened } from '../actions/header-search';
import {
  navigateInPreview,
  navigateProGalleryWithinPostPage,
  navigateToCustomPostUrl,
  navigateToSearch,
  navigateToSectionInPreview,
  navigateWithinBlog,
  navigateWithinPostPage,
} from '../actions/navigate-within-blog';
import { pageOpened } from '../actions/page-opened';
import { postPageMoreButtonClicked } from '../actions/post-page-more-button-clicked';
import { postPageMoreButtonOptionClicked } from '../actions/post-page-more-button-option-clicked';
import { postPageSocialShareClicked } from '../actions/post-page-social-share-clicked';
import { requestLogin, requestLoginPromisified } from '../actions/request-login';
import { searchSubmitted } from '../actions/search';
import trackEvent from '../actions/track-event';
import { BUTTON_ACTION_CLICK } from '../bi-events/bi-buttons';
import { blogAppDefId } from '../constants/apps';
import { SANTA_MEMBERS_APP_ID } from '../constants/members-area';
import { REFERRAL_BLOG_POST_PAYWALL } from '../constants/paid-plans';
import { messageActions } from '../messages/framework/initialize-store';
import { modalActions } from '../modals/framework/initialize-store';
import { type Router } from '../router';
import { getCurrentMatchEncodedPathname } from '../router/router-selectors';
import { isFeedAdapterEnabled, isPostPageAdapterEnabled } from '../selectors/experiment-selectors';
import { getQueryLocale } from '../selectors/locale-selectors';
import { setAppConfig } from '../store/app-config/app-config-actions';
import { SET_APP_SETTINGS, setAppSettings } from '../store/app-settings/app-settings-actions';
import { fetchCurrentUser, fetchCurrentUserPromisified } from '../store/auth/fetch-current-user';
import { setBasicParams } from '../store/basic-params/basic-params-actions';
import fetchCategories from '../store/categories/fetch-categories';
import { setCommunitiesContext } from '../store/communities-context/communities-context-actions';
import { setCustomRoutes } from '../store/custom-routes/custom-routes-actions';
import { setEnableLinkfify } from '../store/editor-config/editor-config-actions';
import { initializeInstanceValues } from '../store/instance-values/instance-values-actions';
import { getInstanceId } from '../store/instance-values/instance-values-selectors';
import { setIsLoaded } from '../store/is-loaded/is-loaded-actions';
import { setIsLoading } from '../store/is-loading/is-loading-actions';
import { setLocale, setQueryLocaleAction } from '../store/locale/locale-actions';
import { initializeMembersNavigation } from '../store/ma-navigation/ma-navigation-actions';
import { paginationClearCollection } from '../store/pagination/pagination-actions';
import { FETCH_TOPOLOGY_SUCCESS, fetchTopology } from '../store/topology/topology-actions';
import { waitForAction } from '../store/wait-for-action';
import { type ControllerConfig } from '../types';
import { doRouting, getInstance } from './helpers';
import { incrementPostLikeCount, requestFileDownloadUrlPromisified } from './lazy-actions';
import { type PlatformApi } from './platform-api';

/** The Members Area public API, which ships no types; only what we call. */
type MembersPublicApi = {
  navigateToMember: (params: {
    memberId: string | null | undefined;
    memberSlug?: string | null;
  }) => Promise<unknown>;
  navigateToSection: (params: Record<string, unknown>) => Promise<unknown>;
};

export function initializeActions({
  platformApi,
  store,
}: {
  platformApi: PlatformApi;
  store: Store;
}) {
  return {
    navigateToProfile: (
      memberId: string | null | undefined,
      memberSlug: string | null | undefined,
    ) => {
      platformApi.site.getPublicAPI(SANTA_MEMBERS_APP_ID).then((api: MembersPublicApi) => {
        api.navigateToMember({ memberId, memberSlug }).catch(console.error);
      });
    },
    navigateToNotifications: () => {
      platformApi.site.getPublicAPI(SANTA_MEMBERS_APP_ID).then((api: MembersPublicApi) => {
        api
          .navigateToSection({
            appDefinitionId: '14f25924-5664-31b2-9568-f9c5ed98c9b1',
            sectionId: 'notifications',
            widgetId: '14f2595a-a352-3ff1-9b3c-4d21861fe58f',
          })
          .catch(console.error);
      });
    },
    navigateToPaidPlans: async (
      postId: string,
      planIds: string[] = [],
      returnUrl = '',
      titleText = '',
      contentText = '',
      buttonText = '',
    ) => {
      const api = await getPricingPlansPublicAPI(platformApi as unknown as IWixAPI);
      const content = omitBy({ title: titleText, message: contentText, cta: buttonText }, isNil);

      api.navigateToPackagePicker({
        planIds: planIds ? planIds : [],
        checkout: {
          thankYouPage: {
            content: !isEmpty(content) ? content : undefined,
            navigation: returnUrl
              ? {
                  type: 'section',
                  options: {
                    sectionId: 'post',
                    appDefinitionId: blogAppDefId,
                    state: returnUrl,
                  },
                }
              : undefined,
          },
        },
        biOptions: {
          referralInfo: REFERRAL_BLOG_POST_PAYWALL,
          referralId: postId,
        },
      });
    },
    renderSEOTags: (data: SeoTagsPayload) => platformApi.seo.renderSEOTags(data),
    ...bindActionCreators(
      {
        ...modalActions,
        ...messageActions,
        actionsClosed,
        actionsOpened,
        buttonClicked: (data) =>
          buttonClicked({ name: BUTTON_ACTION_CLICK, type: 'post', ...data }),
        categoryLabelClicked,
        emitBlogOpened,
        fileDownloaded: biFileDownloaded,
        goBack: () => console.log('goBack'), // TODO: fix or remove
        goToFeed: () => navigateWithinBlog('/'),
        headerSearchClosed,
        headerSearchOpened,
        incrementPostLikeCount,
        navigateInPreview,
        navigateProGalleryWithinPostPage,
        navigateToCustomPostUrl,
        navigateToSearch,
        navigateToSectionInPreview,
        navigateWithinBlog,
        navigateWithinPostPage,
        pageOpened,
        paginationClearCollection,
        postPageMoreButtonClicked,
        postPageMoreButtonOptionClicked,
        postPageSocialShareClicked,
        requestLogin,
        searchSubmitted,
        setIsLoaded,
        setIsLoading,
        trackEvent,
      },
      store.dispatch,
    ),
  };
}

export function initializePromisifiedActions<T extends ActionCreatorsMapObject>(
  {
    store,
  }: {
    store: Store;
  },
  actions: T = {} as T,
) {
  return bindActionCreators(
    {
      fetchArchivePostsWithAdapterPromisified,
      fetchCategoryPostsWithAdapterPromisified,
      fetchFeedPostsWithAdapterPromisified,
      fetchHashtagPostsWithAdapterPromisified,
      fetchMentionsPromisified,
      fetchTagPostsWithAdapterPromisified,
      requestFileDownloadUrlPromisified,
      requestLoginPromisified,
      fetchPostMetadata: fetchPostMetadataPromisified,
      fetchFeedMetadata,
      fetchPostListMetadata,
      fetchCurrentUserPromisified,
      ...actions,
    },
    store.dispatch,
  );
}

export async function initializeStoreBaseData({
  platformApi,
  store,
  language,
  platformAPIs,
  config,
  bundleName,
  preFetch = () => Promise.resolve(),
  appParams,
  route,
}: {
  platformApi: PlatformApi;
  store: Store;
  language: string;
  config: ControllerConfig['config'];
  platformAPIs: ControllerConfig['platformAPIs'];
  appParams: ControllerConfig['appParams'];
  bundleName: string;
  preFetch?: () => Promise<void>;
  route: string | undefined;
}) {
  const instance = getInstance(platformApi)();

  if (!instance) {
    throw new Error('Instance is not defined');
  }

  store.dispatch(initializeInstanceValues(instance));
  store.dispatch(setLocale(language));
  store.dispatch(setQueryLocaleAction(platformApi, appParams));

  const viewMode = platformApi.window.viewMode.toLowerCase();
  const state = store.getState();
  const instanceId = getInstanceId(state);

  const commonActions = [
    store.dispatch(setAppSettings({ style: config.style.styleParams })),
    store.dispatch(
      waitForAction(
        [SET_APP_SETTINGS],
        () =>
          setBasicParams({
            viewMode,
            language,
            biPageNumber: platformAPIs.bi?.pageNumber,
          }),
        true,
      ),
    ),
    store.dispatch(fetchTopology(instanceId)),
    store.dispatch(initializeMembersNavigation()),
    store.dispatch(setCommunitiesContext()),
    store.dispatch(setEnableLinkfify(instanceId)),
    store.dispatch(setCustomRoutes()),
    store.dispatch(
      waitForAction(
        [FETCH_BLOG_DATA_SUCCESS, FETCH_TOPOLOGY_SUCCESS],
        () => setAppConfig({ bundleName }),
        true,
      ),
    ),
  ];

  if (isFeedAdapterEnabled(route)) {
    return Promise.all([...commonActions, preFetch()]);
  }

  if (isPostPageAdapterEnabled(route)) {
    return Promise.all([
      ...commonActions,
      store
        .dispatch(
          fetchTPASettings({
            language: platformApi.window.multilingual.isEnabled
              ? getQueryLocale(state) || language
              : undefined,
            shouldUseFallbackOnError: true,
          }),
        )
        .then(() =>
          store.dispatch(
            fetchAppDataSuccess({
              ampEnabled: store.getState().tpaSettings.settings.ampEnabled,
            }),
          ),
        ),
      preFetch(),
      store.dispatch(fetchCategories()),
    ]);
  }

  return Promise.all([
    ...commonActions,
    store
      .dispatch(
        fetchTPASettings({
          language: platformApi.window.multilingual.isEnabled
            ? getQueryLocale(state) || language
            : undefined,
          shouldUseFallbackOnError: true,
        }),
      )
      .then(() =>
        store.dispatch(
          fetchAppDataSuccess({
            ampEnabled: store.getState().tpaSettings.settings.ampEnabled,
          }),
        ),
      ),
    preFetch(),
    store.dispatch(fetchCategories()),
  ]);
}

export function refreshDataOnLogin({
  platformApi,
  store,
  router,
}: {
  platformApi: PlatformApi;
  store: Store;
  router?: Router;
}) {
  platformApi.user.onLogin(() =>
    setTimeout(async () => {
      await store.dispatch(fetchCurrentUser());

      if (router) {
        const pathname = getCurrentMatchEncodedPathname(store.getState());
        doRouting({ store, router, pathname });
      }
    }),
  );
}
